import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { Bar } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import {
    Layers,
    Clock,
    AlertTriangle,
    Anchor,
    Ship,
    UserX,
    Link2Off,
    TrendingDown,
} from "lucide-react";
import {
    UI,
    toneColor,
    priorityTone,
    safeNumber,
    formatMoney,
    shortLabel,
    fetchExchangeRates,
    convertCurrency,
    StatusPill,
    KpiCard,
    KpiGrid,
    Panel,
    ActionList,
    RankList,
    DonutWithLegend,
    MetricRow,
    SegmentedTabs,
    DashboardToolbar,
    barChartOptions,
    StackedCell,
    DetailsButton,
    TableCard,
    withRawSort,
    dateValue,
    useDateRange,
    downloadCsv,
    csvDate,
    csvMoney,
    PRIORITY_RANK,
    ProgressStat,
    TimelineList,
    relativeDays,
} from "./oceanDashboardUi";
import FileStatusDrawer from "./FileStatusDrawer";
import { TRANSPORT_MODES } from "./transportModes";

const safeText = (value, fallback = "—") => {
    if (value === null || value === undefined || value === "") return fallback;

    if (typeof value === "string" || typeof value === "number") return String(value);

    if (Array.isArray(value)) {
        return value.map((item) => safeText(item, "")).filter(Boolean).join(", ");
    }

    if (typeof value === "object") {
        return (
            value.name ||
            value.companyName ||
            value.label ||
            value.title ||
            value.value ||
            fallback
        );
    }

    return fallback;
};

const ETA_TONES = {
    Delayed: "danger",
    "ETA Passed": "danger",
    "ETA Risk": "warning",
    "ETA This Week": "info",
    "On Schedule": "success",
    "No ETA": "neutral",
};

const ETA_ORDER = Object.keys(ETA_TONES);

const BREAKDOWNS = [
    { value: "line", label: "Line", term: "line" },
    { value: "vesselName", label: "Vessel", term: "vessel" },
    { value: "bookingAgent", label: "Booking Agent" },
    { value: "originAgent", label: "Origin Agent" },
    { value: "destinationAgent", label: "Destination Agent" },
    { value: "freightStatus", label: "Freight Status" },
    { value: "linkedHflRange", label: "Linked HFL" },
    { value: "profitStatus", label: "Profit Status" },
];

function MblDashboard({ mode = "ocean" }) {
    const cfg = TRANSPORT_MODES[mode];
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [currency, setCurrency] = useState("EUR");
    const [exchangeRates, setExchangeRates] = useState(undefined);
    const { dateRange, setDateRange, inRange } = useDateRange();
    const [tableFilters, setTableFilters] = useState([]);
    const [breakdown, setBreakdown] = useState("line");
    const [trendMode, setTrendMode] = useState("volume");
    const [selectedId, setSelectedId] = useState(null);
    const navigate = useNavigate();

    const handleEditMbl = (omblId) => {
        navigate(`${cfg.mbl.editPath}?id=${omblId}`);
    };

    const handleGoShipmentForm = (shipmentId) => {
        navigate(`${cfg.hfl.editPath}?id=${shipmentId}`);
    };

    const tableRef = React.useRef(null);

    const filterTitleMap = {
        line: "Line",
        vesselName: "Vessel",
        bookingAgent: "Booking Agent",
        originAgent: "Origin Agent",
        destinationAgent: "Destination Agent",
        freightStatus: "Freight Status",
        delayStatus: "Delay Status",
        profitStatus: "Profit Status",
        etaRisk: "ETA Status",
        etaRiskGroup: "ETA Risk",
        linkedHflRange: "Linked HFL",
        missingAgent: "Missing Agent",
        delayed: "Delayed MBL",
        etaThisWeek: "ETA This Week",
        negativeMargin: "Negative Margin",
        positiveProfit: "Positive Profit",
    };

    const setFilter = (type, value, label) => {
        const title = filterTitleMap[type] || "Filter";

        const newFilter = {
            type,
            value: value ?? null,
            label: value ? `${title}: ${label || value}` : title,
        };

        setTableFilters((prev) => {
            const exists = prev.some((f) => f.type === newFilter.type && f.value === newFilter.value);

            if (exists) {
                return prev.filter((f) => !(f.type === newFilter.type && f.value === newFilter.value));
            }

            return [...prev, newFilter];
        });

        setTimeout(() => {
            tableRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }, 150);
    };

    const isFilterActive = (type, value = null) =>
        tableFilters.some((f) => f.type === type && (value === null || f.value === value));

    const activeFilterValue = (type) => tableFilters.find((f) => f.type === type)?.value;

    const removeFilter = (filter) => {
        setTableFilters((prev) =>
            prev.filter((f) => !(f.type === filter.type && f.value === filter.value))
        );
    };

    const clearFilter = () => {
        setTableFilters([]);
    };

    useEffect(() => {
        const loadRates = async () => {
            const data = await fetchExchangeRates();
            setExchangeRates(data);
        };

        loadRates();
    }, []);

    useEffect(() => {
        const fetchMbl = async () => {
            try {
                const res = await cfg.mbl.fetch();
                const raw = res?.data?.data || res?.data || [];

                const normalized = raw.map((item) => {
                    const a = item.attributes || item;
                    const shipments = Array.isArray(a.shipments) ? a.shipments : [];
                    const financial = a.financialSummary || {};
                    const freight = a.freightCharges || {};

                    const line = safeText(a.lineDetails?.line);
                    const bookingAgent = safeText(a.selectedBookingAgent);
                    const originAgent = safeText(a.selectedOriginAgent);
                    const destinationAgent = safeText(a.selectedDestinationAgent);

                    const totalCurrency =
                        financial.totalCurrency ||
                        freight.totalCurrency ||
                        a.totalCurrency ||
                        "EUR";

                    const pickupTotal = safeNumber(financial.pickupTotal || freight.pickupTotal);
                    const othcTotal = safeNumber(financial.othcTotal || freight.othc?.total);
                    const oceanFreightTotal = safeNumber(
                        financial[`${cfg.mbl.freightKey}Total`] || freight[cfg.mbl.freightKey]?.total
                    );
                    const dthcTotal = safeNumber(financial.dthcTotal || freight.dthc?.total);
                    const handlingTotal = safeNumber(financial.handlingTotal || freight.handling?.total);
                    const deliveryTotal = safeNumber(financial.deliveryTotal || freight.deliveryTotal);

                    const buyingRate = safeNumber(
                        financial.buyingRate ||
                        freight.buyingRate ||
                        pickupTotal + othcTotal + oceanFreightTotal + dthcTotal + handlingTotal + deliveryTotal
                    );

                    const totalSellingRate = safeNumber(financial.totalSellingRate);
                    const totalProfit = safeNumber(
                        financial.totalProfit || totalSellingRate - buyingRate
                    );

                    const eta = a.eta;
                    const originalEtaDate = a.originalEtaDate;
                    const cob = a.cob;

                    const delayed =
                        originalEtaDate && eta
                            ? dayjs(eta).isAfter(dayjs(originalEtaDate), "day")
                            : false;

                    return {
                        id: item.id || item._id,
                        omblNo: a[cfg.mbl.numberField] || "—",
                        linkedHflCount: shipments.length,
                        line,
                        vesselName: safeText(a.vesselName),
                        bookingAgent,
                        originAgent,
                        destinationAgent,
                        freightStatus: a.freightStatus || "Unknown",
                        bookingResponsibleParty: a.bookingResponsibleParty || "Unknown",
                        originAgentEnabled: a.originAgentEnabled || "",
                        destinationAgentEnabled: a.destinationAgentEnabled || "",
                        finalMblShipper: safeText(a.finalMblShipper),
                        finalMblConsignee: safeText(a.finalMblConsignee),
                        cob,
                        eta,
                        originalEtaDate,
                        rdlDate: a.rdlDate,
                        aadDate: a.aadDate,
                        delayReason: a.delayReason || "",
                        freeTime: a.freeTime,
                        transitTime: a.transitTime,
                        plateContainerNumber: a.plateContainerNumber || "—",
                        pickupTotal,
                        othcTotal,
                        oceanFreightTotal,
                        dthcTotal,
                        deliveryTotal,
                        buyingRate,
                        totalSellingRate,
                        totalProfit,
                        totalCurrency,
                        shipments,
                        delayed,
                    };
                });

                setRows(normalized);
            } catch (error) {
                console.error("MBL dashboard fetch error:", error);
                setRows([]);
            } finally {
                setLoading(false);
            }
        };

        fetchMbl();
    }, []);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();

        const dated = rows.filter((row) => inRange(row.cob));
        if (!q) return dated;

        return dated.filter((row) =>
            [
                row.omblNo,
                row.line,
                row.vesselName,
                row.bookingAgent,
                row.originAgent,
                row.destinationAgent,
                row.freightStatus,
                row.finalMblShipper,
                row.finalMblConsignee,
                row.plateContainerNumber,
            ]
                .join(" ")
                .toLowerCase()
                .includes(q)
        );
    }, [rows, search, dateRange]);

    const getConvertedBuying = (row) =>
        convertCurrency(row.buyingRate, row.totalCurrency, currency, exchangeRates);

    const getConvertedSelling = (row) =>
        convertCurrency(row.totalSellingRate, row.totalCurrency, currency, exchangeRates);

    const getConvertedProfit = (row) => getConvertedSelling(row) - getConvertedBuying(row);

    const getProfitStatus = (row) => {
        const profit = getConvertedProfit(row);

        if (profit > 0) return "Profitable";
        if (profit < 0) return "Loss";
        return "Break-even";
    };

    const getDelayStatus = (row) => {
        if (row.delayed) return "Delayed";
        if (!row.eta) return "No ETA";
        return "On Time";
    };

    const getEtaRisk = (row) => {
        const today = dayjs();

        if (!row.eta) return "No ETA";
        if (row.delayed) return "Delayed";

        const daysLeft = dayjs(row.eta).diff(today, "day");

        if (daysLeft < 0) return "ETA Passed";
        if (daysLeft <= 3) return "ETA Risk";
        if (daysLeft <= 7) return "ETA This Week";

        return "On Schedule";
    };

    const hasMissingAgent = (row) =>
        row.bookingAgent === "—" || row.originAgent === "—" || row.destinationAgent === "—";

    const getPriority = (row) => {
        const profit = getConvertedProfit(row);
        const etaRisk = getEtaRisk(row);

        if (profit < 0) return "Critical";
        if (etaRisk === "Delayed" || etaRisk === "ETA Passed") return "Critical";
        if (hasMissingAgent(row)) return "High";
        if (etaRisk === "ETA Risk") return "High";
        if (etaRisk === "ETA This Week") return "Medium";

        return "Normal";
    };

    const getLinkedHflRange = (row) => {
        if (row.linkedHflCount === 0) return "No HFL";
        if (row.linkedHflCount === 1) return "1 HFL";
        if (row.linkedHflCount <= 3) return "2-3 HFL";
        return "4+ HFL";
    };

    const applyTableFilter = (data) => {
        const today = dayjs();

        if (!tableFilters.length) return data;

        return data.filter((row) =>
            tableFilters.every((filter) => {
                switch (filter.type) {
                    case "line":
                        return row.line === filter.value;

                    case "vesselName":
                        return row.vesselName === filter.value;

                    case "bookingAgent":
                        return row.bookingAgent === filter.value;

                    case "originAgent":
                        return row.originAgent === filter.value;

                    case "destinationAgent":
                        return row.destinationAgent === filter.value;

                    case "freightStatus":
                        return row.freightStatus === filter.value;

                    case "delayStatus":
                        return getDelayStatus(row) === filter.value;

                    case "profitStatus":
                        return getProfitStatus(row) === filter.value;

                    case "etaRisk":
                        return getEtaRisk(row) === filter.value;

                    case "etaRiskGroup":
                        return ["Delayed", "ETA Passed", "ETA Risk"].includes(getEtaRisk(row));

                    case "linkedHflRange":
                        return getLinkedHflRange(row) === filter.value;

                    case "missingAgent":
                        return hasMissingAgent(row);

                    case "delayed":
                        return row.delayed;

                    case "etaThisWeek":
                        return (
                            row.eta &&
                            dayjs(row.eta).diff(today, "day") >= 0 &&
                            dayjs(row.eta).diff(today, "day") <= 7
                        );

                    case "negativeMargin":
                        return getConvertedProfit(row) < 0;

                    case "positiveProfit":
                        return getConvertedProfit(row) > 0;

                    default:
                        return true;
                }
            })
        );
    };

    const tableRows = useMemo(
        () => applyTableFilter(filteredRows),
        [filteredRows, tableFilters, currency, exchangeRates]
    );

    const kpis = useMemo(() => {
        const totalMbl = tableRows.length;
        const linkedHfl = tableRows.reduce((s, r) => s + safeNumber(r.linkedHflCount), 0);
        const totalBuying = tableRows.reduce((s, r) => s + getConvertedBuying(r), 0);
        const totalSelling = tableRows.reduce((s, r) => s + getConvertedSelling(r), 0);
        const totalProfit = totalSelling - totalBuying;

        return {
            totalMbl,
            linkedHfl,
            avgHfl: totalMbl ? linkedHfl / totalMbl : 0,
            delayed: tableRows.filter((r) => r.delayed).length,
            etaPassed: tableRows.filter((r) => getEtaRisk(r) === "ETA Passed").length,
            etaRisk: tableRows.filter((r) =>
                ["Delayed", "ETA Passed", "ETA Risk"].includes(getEtaRisk(r))
            ).length,
            // Filtre ile aynı kural: ETA 0-7 gün içinde
            etaThisWeek: tableRows.filter(
                (r) => r.eta && dayjs(r.eta).diff(dayjs(), "day") >= 0 && dayjs(r.eta).diff(dayjs(), "day") <= 7
            ).length,
            missingAgent: tableRows.filter(hasMissingAgent).length,
            noHfl: tableRows.filter((r) => r.linkedHflCount === 0).length,
            negativeMargin: tableRows.filter((r) => getConvertedProfit(r) < 0).length,
            totalBuying,
            totalSelling,
            totalProfit,
            margin: totalSelling ? (totalProfit / totalSelling) * 100 : 0,
        };
    }, [tableRows, currency, exchangeRates]);

    const groupBy = (getter, { limit = 8, sort = true } = {}) => {
        const map = {};

        filteredRows.forEach((row) => {
            const label = safeText(getter(row), "Unknown");
            map[label] = (map[label] || 0) + 1;
        });

        const list = Object.entries(map).map(([label, count]) => ({ label, count }));
        return sort ? list.sort((a, b) => b.count - a.count).slice(0, limit) : list;
    };

    const etaRows = useMemo(
        () =>
            groupBy(getEtaRisk, { sort: false })
                .sort((a, b) => ETA_ORDER.indexOf(a.label) - ETA_ORDER.indexOf(b.label))
                .map((d) => ({ ...d, color: toneColor(ETA_TONES[d.label]) })),
        [filteredRows]
    );

    const breakdownRows = useMemo(() => {
        switch (breakdown) {
            case "linkedHflRange":
                return ["No HFL", "1 HFL", "2-3 HFL", "4+ HFL"]
                    .map((label) => ({
                        label,
                        count: filteredRows.filter((r) => getLinkedHflRange(r) === label).length,
                        color: label === "No HFL" ? UI.orange : undefined,
                    }));

            case "profitStatus":
                return [
                    ["Profitable", "success"],
                    ["Break-even", "neutral"],
                    ["Loss", "danger"],
                ].map(([label, tone]) => ({
                    label,
                    count: filteredRows.filter((r) => getProfitStatus(r) === label).length,
                    color: toneColor(tone),
                }));

            default:
                return groupBy((row) => row[breakdown]);
        }
    }, [filteredRows, breakdown, currency, exchangeRates]);

    const monthlyRows = useMemo(() => {
        const map = {};

        filteredRows.forEach((row) => {
            if (!row.cob) return;
            const label = dayjs(row.cob).format("YYYY-MM");
            map[label] = map[label] || { count: 0, profit: 0 };
            map[label].count += 1;
            map[label].profit += getConvertedProfit(row);
        });

        return Object.entries(map)
            .map(([label, v]) => ({ label, ...v }))
            .sort((a, b) => a.label.localeCompare(b.label))
            .slice(-12);
    }, [filteredRows, currency, exchangeRates]);

    const actionItems = [
        { key: "delayed", label: "Delayed MBL", description: "ETA later than original ETA", icon: Clock, tone: "danger", count: kpis.delayed },
        { key: "etaRiskGroup", label: "ETA risk", description: "Delayed, passed or within 3 days", icon: AlertTriangle, tone: "warning", count: kpis.etaRisk },
        { key: "missingAgent", label: "Missing agent", description: "Booking, origin or destination agent empty", icon: UserX, tone: "warning", count: kpis.missingAgent },
        { key: "linkedHflRange", value: "No HFL", label: "No linked HFL", description: "Master file without house files", icon: Link2Off, tone: "warning", count: kpis.noHfl },
        { key: "negativeMargin", value: true, label: "Negative margin", description: "Buying exceeds selling", icon: TrendingDown, tone: "danger", count: kpis.negativeMargin },
    ].map((item) => ({
        ...item,
        active: isFilterActive(item.key, item.value ?? null),
        onClick: () => setFilter(item.key, item.value ?? null),
    }));

    // Önümüzdeki 14 gün içindeki (veya gecikmiş) yükleme (COB) ve varış (ETA) hareketleri
    const upcomingMovements = useMemo(() => {
        const events = [];
        const inWindow = (date, from) => {
            const d = dayjs(date).startOf("day").diff(dayjs().startOf("day"), "day");
            return d >= from && d <= 14;
        };

        tableRows.forEach((row) => {
            if (row.cob && inWindow(row.cob, 0)) {
                events.push({ row, kind: "departure", date: row.cob });
            }
            if (row.eta && !row.aadDate && inWindow(row.eta, -3)) {
                events.push({ row, kind: "arrival", date: row.eta });
            }
        });

        return events
            .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
            .slice(0, 6)
            .map(({ row, kind, date }) => {
                const overdue = dayjs(date).isBefore(dayjs(), "day");
                const hflCount = row.linkedHflCount;
                return {
                    key: `${row.id}-${kind}`,
                    icon: kind === "departure" ? Ship : Anchor,
                    tone: overdue || row.delayed ? "danger" : kind === "departure" ? "teal" : "info",
                    title: `${row.omblNo} · ${kind === "departure" ? "Cargo on board" : "Arrival"}`,
                    description: `${row.line} · ${row.vesselName} · ${hflCount} HFL`,
                    when: overdue ? `Overdue · ${relativeDays(date)}` : relativeDays(date),
                    whenColor: overdue || row.delayed ? UI.red : UI.text,
                    date: dayjs(date).format("DD MMM"),
                    onClick: () => handleEditMbl(row.id),
                };
            });
    }, [tableRows]);

    const monthDelta = useMemo(() => {
        const thisMonth = dayjs().format("YYYY-MM");
        const lastMonth = dayjs().subtract(1, "month").format("YYYY-MM");
        const count = (m) => filteredRows.filter((r) => r.cob && dayjs(r.cob).format("YYYY-MM") === m).length;
        return count(thisMonth) - count(lastMonth);
    }, [filteredRows]);

    const withEta = tableRows.filter((r) => r.eta);
    const health = {
        total: tableRows.length,
        agentsAssigned: tableRows.filter((r) => !hasMissingAgent(r)).length,
        linked: tableRows.filter((r) => r.linkedHflCount > 0).length,
        etaTotal: withEta.length,
        onSchedule: withEta.filter((r) => ["On Schedule", "ETA This Week", "ETA Risk"].includes(getEtaRisk(r))).length,
        profitable: tableRows.filter((r) => getConvertedProfit(r) > 0).length,
    };

    const linkSx = {
        color: UI.blue,
        cursor: "pointer",
        fontWeight: 600,
        fontSize: 13,
        "&:hover": { textDecoration: "underline" },
    };

    const subLine = (text) => (
        <MDTypography component="div" sx={{ fontSize: 11.5, color: UI.muted, lineHeight: 1.4 }}>
            {text}
        </MDTypography>
    );

    const hflNoOf = (shipment, index) => {
        const shipmentRow = shipment?.attributes || shipment;
        return shipmentRow?.shipmentNo ? `${cfg.hfl.prefix}${shipmentRow.shipmentNo}` : `${cfg.hfl.prefix}-${index + 1}`;
    };

    const hflIdOf = (shipment) => {
        const shipmentRow = shipment?.attributes || shipment;
        return shipmentRow?.id || shipmentRow?._id || shipment?.id || shipment?._id;
    };

    const getPriorityReasons = (row) => {
        const reasons = [];
        const etaRisk = getEtaRisk(row);
        const missingAgents = [
            ["Booking", row.bookingAgent],
            ["Origin", row.originAgent],
            ["Destination", row.destinationAgent],
        ]
            .filter(([, v]) => v === "—")
            .map(([k]) => k);

        if (getConvertedProfit(row) < 0) reasons.push("Negative margin: buying exceeds selling");
        if (etaRisk === "Delayed") {
            reasons.push(
                `ETA moved from ${dayjs(row.originalEtaDate).format("DD MMM")} to ${dayjs(row.eta).format("DD MMM")}${row.delayReason ? ` · ${row.delayReason}` : ""}`
            );
        }
        if (etaRisk === "ETA Passed") reasons.push("ETA date has passed");
        if (etaRisk === "ETA Risk") reasons.push("Arrival within 3 days");
        if (etaRisk === "ETA This Week") reasons.push("Arrival within 7 days");
        if (missingAgents.length) reasons.push(`Missing agent: ${missingAgents.join(", ")}`);

        return reasons.length ? reasons : ["No open issues on this file"];
    };

    // Detay panelinde gösterilecek takvim / acente / bağlı HFL / finans özeti
    const buildStatus = (row) => {
        const etaRisk = getEtaRisk(row);
        const priority = getPriority(row);
        const selling = getConvertedSelling(row);
        const buying = getConvertedBuying(row);
        const fmt = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—");

        const steps = [
            {
                label: "Cargo on board",
                done: Boolean(row.cob) && !dayjs(row.cob).isAfter(dayjs(), "day"),
                date: row.cob ? dayjs(row.cob).format("DD MMM") : null,
            },
            {
                label: "Arrived at destination",
                done: Boolean(row.aadDate),
                date: row.aadDate
                    ? dayjs(row.aadDate).format("DD MMM")
                    : row.eta
                        ? `ETA ${dayjs(row.eta).format("DD MMM")}`
                        : null,
            },
        ];
        const currentIndex = steps.findIndex((step) => !step.done);

        return {
            title: row.omblNo,
            subtitle:
                [row.line, row.vesselName].filter((v) => v && v !== "—").join(" · ") ||
                `No ${cfg.terms.line.toLowerCase()} / ${cfg.terms.vessel.toLowerCase()} assigned`,
            priority,
            pills: [
                { label: etaRisk, tone: ETA_TONES[etaRisk] },
                { label: `${priority} priority`, tone: priorityTone(priority) },
            ],
            facts: [
                ["COB", fmt(row.cob)],
                ["ETA", fmt(row.eta)],
                ["Original ETA", fmt(row.originalEtaDate)],
                ["Transit time", row.transitTime ? `${row.transitTime} days` : "—"],
                ["Shipper", row.finalMblShipper],
                ["Consignee", row.finalMblConsignee],
            ],
            reasons: getPriorityReasons(row),
            milestonesTitle: "Schedule",
            milestones: steps.map((step, i) => ({ ...step, current: i === currentIndex })),
            checklists: [
                {
                    title: "Agents",
                    items: [
                        ["Booking agent", row.bookingAgent],
                        ["Origin agent", row.originAgent],
                        ["Destination agent", row.destinationAgent],
                    ].map(([name, value]) => ({ name, ok: value !== "—", value })),
                },
            ],
            links: {
                title: `Linked ${cfg.hfl.prefix}`,
                empty: `No ${cfg.hfl.prefix} linked to this master file`,
                items: (row.shipments || []).map((shipment, index) => ({
                    label: hflNoOf(shipment, index),
                    onClick: () => handleGoShipmentForm(hflIdOf(shipment)),
                })),
            },
            financials: { selling, buying, profit: selling - buying },
            openLabel: `Open ${cfg.mbl.label} file`,
            onOpen: () => handleEditMbl(row.id),
        };
    };

    const csvColumns = [
        [`${cfg.mbl.label} No`, (r) => r.omblNo],
        [`Linked ${cfg.hfl.prefix}`, (r) => (r.shipments || []).map(hflNoOf).join(", ")],
        [cfg.terms.line, (r) => r.line],
        [cfg.terms.vessel, (r) => r.vesselName],
        ["Booking agent", (r) => r.bookingAgent],
        ["Origin agent", (r) => r.originAgent],
        ["Destination agent", (r) => r.destinationAgent],
        ["COB", (r) => csvDate(r.cob)],
        ["ETA", (r) => csvDate(r.eta)],
        ["Original ETA", (r) => csvDate(r.originalEtaDate)],
        ["ETA status", (r) => getEtaRisk(r)],
        ["Priority", (r) => getPriority(r)],
        [`Buying (${currency})`, (r) => csvMoney(getConvertedBuying(r))],
        [`Selling (${currency})`, (r) => csvMoney(getConvertedSelling(r))],
        [`Profit (${currency})`, (r) => csvMoney(getConvertedProfit(r))],
    ];

    const selectedRow = rows.find((r) => r.id === selectedId);

    const table = {
        columns: withRawSort([
            { Header: `${cfg.mbl.label} No`, accessor: "omblNo" },
            { Header: `Linked ${cfg.hfl.prefix}`, accessor: "linkedHflCount" },
            { Header: `${cfg.terms.line} / ${cfg.terms.vessel}`, accessor: "line" },
            { Header: "ETA", accessor: "eta" },
            { Header: "Buying", accessor: "buyingRate" },
            { Header: "Selling", accessor: "totalSellingRate" },
            { Header: "Profit", accessor: "totalProfit" },
            { Header: "Status", accessor: "status" },
        ]),
        rows: tableRows.map((row) => {
            const buying = getConvertedBuying(row);
            const selling = getConvertedSelling(row);
            const profit = selling - buying;
            const margin = selling ? (profit / selling) * 100 : 0;
            const priority = getPriority(row);

            return {
                _sort: {
                    omblNo: row.omblNo,
                    linkedHflCount: row.linkedHflCount,
                    line: row.line,
                    eta: dateValue(row.eta),
                    buyingRate: buying,
                    totalSellingRate: selling,
                    totalProfit: profit,
                    status: PRIORITY_RANK[priority],
                },
                omblNo: (
                    <MDTypography component="span" sx={linkSx} onClick={() => handleEditMbl(row.id)}>
                        {row.omblNo}
                    </MDTypography>
                ),
                linkedHflCount: row.shipments?.length ? (
                    <MDBox display="flex" flexDirection="column" gap={0.25}>
                        {row.shipments.map((shipment, index) => (
                            <MDTypography
                                key={`${hflIdOf(shipment) || "hfl"}-${index}`}
                                component="span"
                                sx={{ ...linkSx, fontSize: 12.5, display: "block" }}
                                onClick={() => handleGoShipmentForm(hflIdOf(shipment))}
                            >
                                {hflNoOf(shipment, index)}
                            </MDTypography>
                        ))}
                    </MDBox>
                ) : (
                    <StatusPill label={`No ${cfg.hfl.prefix}`} tone="warning" />
                ),
                line: (
                    <StackedCell
                        labelWidth={58}
                        lines={[
                            [cfg.terms.line, row.line],
                            [cfg.terms.vessel, row.vesselName],
                        ]}
                    />
                ),
                eta: row.eta ? dayjs(row.eta).format("DD/MM/YYYY") : "—",
                buyingRate: formatMoney(buying, currency),
                totalSellingRate: formatMoney(selling, currency),
                totalProfit: (
                    <MDBox>
                        <MDTypography
                            component="div"
                            sx={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: profit > 0 ? UI.green : profit < 0 ? UI.red : UI.muted,
                            }}
                        >
                            {formatMoney(profit, currency)}
                        </MDTypography>
                        {subLine(`${margin.toFixed(1)}% margin`)}
                    </MDBox>
                ),
                status: (
                    <DetailsButton
                        priority={priority}
                        hint={`${getEtaRisk(row)} · ${priority} priority`}
                        onClick={() => setSelectedId(row.id)}
                    />
                ),
            };
        }),
    };

    if (loading) {
        return (
            <MDBox height="50vh" display="flex" alignItems="center" justifyContent="center">
                <CircularProgress />
            </MDBox>
        );
    }

    return (
        <MDBox display="flex" flexDirection="column" gap={2}>
            <DashboardToolbar
                search={search}
                onSearch={setSearch}
                placeholder="Search MBL, line, vessel, agent, shipper, consignee, container..."
                currency={currency}
                onCurrency={setCurrency}
                filters={tableFilters}
                onRemoveFilter={removeFilter}
                onClear={clearFilter}
                dateRange={dateRange}
                onDateRange={setDateRange}
                dateLabel="COB"
                ratesMissing={exchangeRates === null}
                resultLabel={`${tableRows.length} MBL files`}
            />

            <KpiGrid>
                <KpiCard
                    label="Master Files"
                    value={kpis.totalMbl}
                    trend={{ delta: monthDelta, label: `COB this month vs last · avg ${kpis.avgHfl.toFixed(1)} HFL` }}
                    icon={Layers}
                    tone="neutral"
                    onClick={clearFilter}
                />
                <KpiCard
                    label="Delayed"
                    value={kpis.delayed}
                    hint="ETA later than original ETA"
                    icon={Clock}
                    tone="danger"
                    active={isFilterActive("delayed")}
                    onClick={() => setFilter("delayed")}
                />
                <KpiCard
                    label="ETA Risk"
                    value={kpis.etaRisk}
                    hint="Delayed, passed or ≤ 3 days"
                    icon={AlertTriangle}
                    tone="warning"
                    active={isFilterActive("etaRiskGroup")}
                    onClick={() => setFilter("etaRiskGroup")}
                />
                <KpiCard
                    label="Arriving ≤ 7 Days"
                    value={kpis.etaThisWeek}
                    hint="ETA within the next week"
                    icon={Anchor}
                    tone="info"
                    active={isFilterActive("etaThisWeek")}
                    onClick={() => setFilter("etaThisWeek")}
                />
                <KpiCard
                    label="Missing Agent"
                    value={kpis.missingAgent}
                    hint="Booking / origin / destination"
                    icon={UserX}
                    tone="warning"
                    active={isFilterActive("missingAgent")}
                    onClick={() => setFilter("missingAgent")}
                />
            </KpiGrid>

            <Grid container spacing={2}>
                <Grid item xs={12} lg={4}>
                    <Panel title="ETA Status" subtitle="Click a status to filter the table">
                        <DonutWithLegend
                            data={etaRows}
                            totalLabel="MBL"
                            activeValue={activeFilterValue("etaRisk")}
                            onSelect={(label) => setFilter("etaRisk", label)}
                        />
                    </Panel>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Panel title="Action Center" subtitle="Items needing operational follow-up" bodySx={{ py: 1 }}>
                        <ActionList items={actionItems} />
                    </Panel>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Panel title="Financial Summary" subtitle={`Filtered MBL files · ${currency}`}>
                        <MetricRow label="Selling" value={formatMoney(kpis.totalSelling, currency)} />
                        <MetricRow label="Buying" value={formatMoney(kpis.totalBuying, currency)} />
                        <MetricRow
                            label="Gross Profit"
                            value={formatMoney(kpis.totalProfit, currency)}
                            valueColor={kpis.totalProfit >= 0 ? UI.green : UI.red}
                            strong
                        />
                        <MetricRow
                            label="Margin"
                            value={`${kpis.margin.toFixed(1)}%`}
                            valueColor={kpis.margin >= 0 ? UI.text : UI.red}
                        />
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={7}>
                    <Panel
                        title="Upcoming Movements"
                        subtitle="Cargo on board and arrivals in the next 14 days · click to open the MBL"
                        bodySx={{ py: 1 }}
                    >
                        <TimelineList items={upcomingMovements} emptyText="No COB or arrivals in the next 14 days" />
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={5}>
                    <Panel title="Operational Health" subtitle="Share of MBL files in view">
                        <MDBox display="flex" flexDirection="column" gap={2}>
                            <ProgressStat
                                label="Agents assigned"
                                value={health.agentsAssigned}
                                total={health.total}
                                tone={health.agentsAssigned / (health.total || 1) >= 0.8 ? "success" : "warning"}
                                onClick={() => setFilter("missingAgent")}
                            />
                            <ProgressStat label="Linked to HFL" value={health.linked} total={health.total} tone="teal" />
                            <ProgressStat
                                label="ETA on schedule (files with ETA)"
                                value={health.onSchedule}
                                total={health.etaTotal}
                                tone="info"
                            />
                            <ProgressStat label="Profitable" value={health.profitable} total={health.total} tone="success" />
                        </MDBox>
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={7}>
                    <Panel title="Breakdown" subtitle="Top 8 · click a row to filter the table">
                        <MDBox mb={2}>
                            <SegmentedTabs
                                options={BREAKDOWNS.map((b) => (b.term ? { ...b, label: cfg.terms[b.term] } : b))}
                                value={breakdown}
                                onChange={setBreakdown}
                            />
                        </MDBox>
                        <RankList
                            data={breakdownRows}
                            activeValue={activeFilterValue(breakdown)}
                            onSelect={(label) => setFilter(breakdown, label)}
                        />
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={5}>
                    <Panel
                        title="Monthly Trend"
                        subtitle="By COB month (last 12)"
                        action={
                            <SegmentedTabs
                                options={[
                                    { value: "volume", label: "Volume" },
                                    { value: "profit", label: "Profit" },
                                ]}
                                value={trendMode}
                                onChange={setTrendMode}
                            />
                        }
                    >
                        <MDBox height={300}>
                            <Bar
                                data={{
                                    labels: monthlyRows.map((x) => dayjs(`${x.label}-01`).format("MMM YY")),
                                    datasets: [
                                        trendMode === "volume"
                                            ? {
                                                label: "MBL files",
                                                data: monthlyRows.map((x) => x.count),
                                                backgroundColor: UI.teal,
                                                borderRadius: 4,
                                                maxBarThickness: 28,
                                            }
                                            : {
                                                label: `Profit (${currency})`,
                                                data: monthlyRows.map((x) => Number(x.profit.toFixed(2))),
                                                backgroundColor: monthlyRows.map((x) => (x.profit >= 0 ? UI.green : UI.red)),
                                                borderRadius: 4,
                                                maxBarThickness: 28,
                                            },
                                    ],
                                }}
                                options={barChartOptions}
                            />
                        </MDBox>
                    </Panel>
                </Grid>
            </Grid>

            <TableCard
                cardRef={tableRef}
                onExport={() => downloadCsv(`${cfg.mbl.label}-files`, csvColumns, tableRows)}
                title={`${cfg.mbl.label} File Control`}
                summary={
                    tableFilters.length
                        ? `${tableRows.length} of ${filteredRows.length} ${cfg.mbl.label} files · ${tableFilters.map((f) => shortLabel(f.label, 40)).join(", ")}`
                        : `All ${cfg.mbl.label} files · ${tableRows.length}`
                }
            >
                <DataTable
                    table={table}
                    entriesPerPage={{ defaultValue: 25, entries: [25, 50, 100] }}
                    showTotalEntries
                    isSorted
                    noEndBorder
                />
            </TableCard>

            <FileStatusDrawer
                status={selectedRow ? buildStatus(selectedRow) : null}
                currency={currency}
                onClose={() => setSelectedId(null)}
            />
        </MDBox>
    );
}

export default MblDashboard;
